import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import {
  useProposalDetail,
  useProposalVersions,
  useProposalReviews,
  useTransitionProposal,
  useGenerateAiDraft,
} from '@/hooks/useProposal';
import { ProposalStatusBadge } from '@/components/proposal/ProposalStatusBadge';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { PROPOSAL_PERMISSIONS, hasPermission } from '@/lib/proposalPermissions';
import type { ApiComponents } from '@jinzhuan/api-client';
import {
  useIntakeAssessmentApprovedSummary,
  INTAKE_ASSESSMENT_KIND_LABELS,
} from '@/hooks/useIntakeAssessment';
import {
  INTAKE_ASSESSMENT_PERMISSIONS,
  hasPermission as hasIntakePermission,
} from '@/lib/intakeAssessmentPermissions';
import ProposalQuoteContractPanel from '@/pages/quote-contract/ProposalQuoteContractPanel';

type ReviewRole = ApiComponents['schemas']['CreateProposalReviewDto']['reviewRole'];
type ReviewResult = ApiComponents['schemas']['CreateProposalReviewDto']['reviewResult'];

const REVIEW_ROLE_LABELS: Record<ReviewRole, string> = {
  doctor: '医生',
  technical_lead: '技术负责人',
  compliance: '合规专员',
};

const REVIEW_RESULT_LABELS: Record<ReviewResult, string> = {
  approved: '批准',
  rejected: '拒绝',
  changes_requested: '要求修改',
};

// ============================================================================
// Submit Review Dialog
// ============================================================================

interface SubmitReviewDialogProps {
  proposalId: string;
  proposalVersionId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function SubmitReviewDialog({
  proposalId,
  proposalVersionId,
  onClose,
  onSuccess,
}: SubmitReviewDialogProps) {
  const [reviewRole, setReviewRole] = useState<ReviewRole>('doctor');
  const [reviewResult, setReviewResult] = useState<ReviewResult>('approved');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Import submitReview from hook
  const { submitReview } = useProposalReviews();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    try {
      const result = await submitReview(proposalId, {
        proposalVersionId,
        reviewRole,
        reviewResult,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      });
      if (result) {
        onSuccess();
      } else {
        setSubmitError('提交审核失败，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg space-y-4">
        <h2 className="text-lg font-semibold">提交审核意见</h2>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">审核角色</label>
            <select
              value={reviewRole}
              onChange={(e) => setReviewRole(e.target.value as ReviewRole)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {(Object.keys(REVIEW_ROLE_LABELS) as ReviewRole[]).map((role) => (
                <option key={role} value={role}>
                  {REVIEW_ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">审核结果</label>
            <select
              value={reviewResult}
              onChange={(e) => setReviewResult(e.target.value as ReviewResult)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {(Object.keys(REVIEW_RESULT_LABELS) as ReviewResult[]).map((result) => (
                <option key={result} value={result}>
                  {REVIEW_RESULT_LABELS[result]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">审核意见（可选）</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="请输入审核意见..."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          {submitError && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {submitError}
            </div>
          )}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? '提交中...' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

export default function ProposalDetailPage() {
  const [, navigate] = useLocation();
  const params = useParams<{ proposalId: string }>();
  const { permissions } = useAuth();

  const { proposal, loading, error, fetchProposal } = useProposalDetail();
  const { versions, loading: versionsLoading, fetchVersions } = useProposalVersions();
  const { reviews, loading: reviewsLoading, fetchReviews } = useProposalReviews();
  const { submitForReview, lockProposal, loading: actionLoading, error: actionError } = useTransitionProposal();
  const { generateAiDraft, loading: aiLoading, error: aiError } = useGenerateAiDraft();
  const {
    summary: intakeSummary,
    loading: intakeLoading,
    fetchSummary: fetchIntakeSummary,
  } = useIntakeAssessmentApprovedSummary();

  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState('');

  const canRead = hasPermission(permissions, PROPOSAL_PERMISSIONS.PROPOSAL_READ);
  const canUpdate = hasPermission(permissions, PROPOSAL_PERMISSIONS.PROPOSAL_UPDATE);
  const canLock = hasPermission(permissions, PROPOSAL_PERMISSIONS.PROPOSAL_LOCK);
  const canReview = hasPermission(permissions, PROPOSAL_PERMISSIONS.REVIEW_CREATE);
  const canReadIntake = hasIntakePermission(
    permissions,
    INTAKE_ASSESSMENT_PERMISSIONS.READ,
  );

  const proposalId = params.proposalId;

  useEffect(() => {
    if (!canRead || !proposalId) return;
    void fetchProposal(proposalId);
    void fetchVersions(proposalId);
    void fetchReviews(proposalId);
  }, [proposalId, fetchProposal, fetchVersions, fetchReviews, canRead]);

  useEffect(() => {
    if (!canReadIntake || !proposal?.customerId) return;
    void fetchIntakeSummary(proposal.customerId);
  }, [canReadIntake, proposal?.customerId, fetchIntakeSummary]);

  if (!canRead) {
    return <AccessDenied resource="方案详情" />;
  }

  async function handleSubmitForReview() {
    if (!proposal || !proposalId) return;
    if (!confirm('确认将方案提交审核？')) return;
    const result = await submitForReview(proposalId, { expectedVersion: proposal.version });
    if (result) {
      void fetchProposal(proposalId);
    }
  }

  async function handleLock() {
    if (!proposal || !proposalId) return;
    if (!confirm('确认锁定方案？锁定后将无法修改。')) return;
    const result = await lockProposal(proposalId, { expectedVersion: proposal.version });
    if (result) {
      void fetchProposal(proposalId);
    }
  }

  function buildApprovedIntakeContext(): string | undefined {
    if (!intakeSummary) return undefined;
    const lines: string[] = [];
    const entries: Array<['goldenAnalysis' | 'dealIntention' | 'healthAssessment', string]> = [
      ['goldenAnalysis', INTAKE_ASSESSMENT_KIND_LABELS.golden_analysis],
      ['dealIntention', INTAKE_ASSESSMENT_KIND_LABELS.deal_intention],
      ['healthAssessment', INTAKE_ASSESSMENT_KIND_LABELS.health_assessment],
    ];
    for (const [key, label] of entries) {
      const entry = intakeSummary[key];
      if (entry?.approvedSummary) {
        lines.push(`【${label}】${entry.approvedSummary}`);
      }
    }
    if (lines.length === 0) return undefined;
    return lines.join('\n');
  }

  async function handleGenerateAiDraft() {
    if (!proposalId) return;
    const contextHint = buildApprovedIntakeContext();
    const result = await generateAiDraft(
      proposalId,
      contextHint ? { contextHint } : {},
    );
    if (result) {
      void fetchVersions(proposalId);
    }
  }

  function handleReviewSuccess() {
    setShowReviewDialog(false);
    setSelectedVersionId('');
    if (proposalId) {
      void fetchProposal(proposalId);
      void fetchReviews(proposalId);
    }
  }

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">加载中...</div>;
  }

  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
        加载失败：{error.message}
      </div>
    );
  }

  if (!proposal) {
    return <div className="text-center py-8 text-muted-foreground">方案不存在</div>;
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/proposals')}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← 返回列表
        </button>
        <h1 className="text-xl font-semibold">{proposal.title}</h1>
        <ProposalStatusBadge status={proposal.status} />
      </div>

      {/* Action errors */}
      {(actionError || aiError) && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          操作失败：{actionError?.message ?? aiError?.message}
        </div>
      )}

      {/* Proposal metadata */}
      <div className="rounded-lg border p-4 space-y-3">
        <h2 className="text-base font-medium">基本信息</h2>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-muted-foreground">方案 ID</dt>
            <dd className="font-mono text-xs mt-0.5">{proposal.id}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">客户 ID</dt>
            <dd className="font-mono text-xs mt-0.5">{proposal.customerId}</dd>
          </div>
          {proposal.orderId && (
            <div>
              <dt className="text-muted-foreground">关联订单</dt>
              <dd className="font-mono text-xs mt-0.5">{proposal.orderId}</dd>
            </div>
          )}
          <div>
            <dt className="text-muted-foreground">版本号</dt>
            <dd className="mt-0.5">{proposal.version}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">创建时间</dt>
            <dd className="mt-0.5">
              {new Date(proposal.createdAt).toLocaleString('zh-CN')}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">更新时间</dt>
            <dd className="mt-0.5">
              {new Date(proposal.updatedAt).toLocaleString('zh-CN')}
            </dd>
          </div>
        </dl>
      </div>

      {canReadIntake && (
        <div className="rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-medium">接诊评估摘要（已批准）</h2>
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() =>
                navigate(
                  `/intake-assessment/records?customerId=${encodeURIComponent(
                    proposal.customerId,
                  )}`,
                )
              }
            >
              查看全部记录 →
            </button>
          </div>
          {intakeLoading && !intakeSummary ? (
            <p className="mt-2 text-sm text-muted-foreground">加载中…</p>
          ) : intakeSummary ? (
            <ul className="mt-2 space-y-1 text-sm">
              {(['goldenAnalysis', 'dealIntention', 'healthAssessment'] as const).map(
                (key) => {
                  const entry = intakeSummary[key];
                  const labelKey =
                    key === 'goldenAnalysis'
                      ? 'golden_analysis'
                      : key === 'dealIntention'
                        ? 'deal_intention'
                        : 'health_assessment';
                  return (
                    <li key={key} className="flex items-start gap-2">
                      <span className="min-w-[6rem] font-medium">
                        {INTAKE_ASSESSMENT_KIND_LABELS[labelKey]}
                      </span>
                      {entry ? (
                        <span className="text-foreground">
                          {entry.approvedSummary}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {new Date(entry.approvedAt).toLocaleDateString('zh-CN')}
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">未批准</span>
                      )}
                    </li>
                  );
                },
              )}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              该客户暂无已批准的接诊评估记录。AI 草稿将不携带接诊评估上下文。
            </p>
          )}
        </div>
      )}

      {/* Quote / Contract summary — Issue #327 */}
      <ProposalQuoteContractPanel
        proposalId={proposal.id}
        proposalVersionId={
          proposal.lockedVersionId ?? proposal.approvedVersionId ?? undefined
        }
        proposalStatus={proposal.status}
      />

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {canUpdate && proposal.status === 'draft' && (
          <>
            <button
              onClick={() => void handleGenerateAiDraft()}
              disabled={aiLoading}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {aiLoading ? 'AI 生成中...' : '✨ AI 生成草稿'}
            </button>
            <button
              onClick={() => void handleSubmitForReview()}
              disabled={actionLoading}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {actionLoading ? '提交中...' : '提交审核'}
            </button>
          </>
        )}
        {canLock && proposal.status === 'approved' && (
          <button
            onClick={() => void handleLock()}
            disabled={actionLoading}
            className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {actionLoading ? '锁定中...' : '锁定方案'}
          </button>
        )}
        {canReview && proposal.status === 'in_review' && versions.length > 0 && (
          <button
            onClick={() => {
              const latestVersion = versions[versions.length - 1];
              if (latestVersion) {
                setSelectedVersionId(latestVersion.id);
                setShowReviewDialog(true);
              }
            }}
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            提交审核意见
          </button>
        )}
      </div>

      {/* Versions */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium">方案版本</h2>
          {versionsLoading && (
            <span className="text-xs text-muted-foreground">加载中...</span>
          )}
        </div>
        {versions.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无版本记录</p>
        ) : (
          <div className="space-y-3">
            {versions.map((version) => (
              <div key={version.id} className="rounded-md border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">版本 {version.versionNo}</span>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="rounded-full bg-muted px-2 py-0.5">
                      {version.source === 'ai_draft' ? 'AI 生成' : '手动编辑'}
                    </span>
                    <span>{new Date(version.createdAt).toLocaleString('zh-CN')}</span>
                  </div>
                </div>
                <pre className="text-xs bg-muted/50 rounded p-2 whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {version.content}
                </pre>
                {canReview && proposal.status === 'in_review' && (
                  <button
                    onClick={() => {
                      setSelectedVersionId(version.id);
                      setShowReviewDialog(true);
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    对此版本提交审核意见
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reviews */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium">审核记录</h2>
          {reviewsLoading && (
            <span className="text-xs text-muted-foreground">加载中...</span>
          )}
        </div>
        {reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无审核记录</p>
        ) : (
          <div className="space-y-2">
            {reviews.map((review) => (
              <div key={review.id} className="flex items-start gap-3 rounded-md border p-3">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {REVIEW_ROLE_LABELS[review.reviewRole as ReviewRole] ?? review.reviewRole}
                    </span>
                    <span
                      className={
                        review.reviewResult === 'approved'
                          ? 'text-xs text-green-600 font-medium'
                          : review.reviewResult === 'rejected'
                          ? 'text-xs text-red-600 font-medium'
                          : 'text-xs text-yellow-600 font-medium'
                      }
                    >
                      {REVIEW_RESULT_LABELS[review.reviewResult as ReviewResult] ?? review.reviewResult}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(review.reviewedAt).toLocaleString('zh-CN')}
                    </span>
                  </div>
                  {review.comment && (
                    <p className="text-sm text-muted-foreground">{review.comment}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Dialog — key forces remount when selectedVersionId changes */}
      {showReviewDialog && selectedVersionId && proposalId && (
        <SubmitReviewDialog
          key={selectedVersionId}
          proposalId={proposalId}
          proposalVersionId={selectedVersionId}
          onClose={() => {
            setShowReviewDialog(false);
            setSelectedVersionId('');
          }}
          onSuccess={handleReviewSuccess}
        />
      )}
    </div>
  );
}
